//Framework
import { Fragment, useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import cx from 'classnames';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { Role, ChangeAffectOptions } from '../../../common/constants';
import { useAppSelector } from '../../../app/hooks';
import Select from 'react-select';
import { graySelectStyles } from '../../../common/selectStyles';

//Services
import { submitTimesheet } from '../../../api/services/timesheetService';
import { hasDecreasedHours } from '../../../api/services/timesheetApprovalProofingService';
import { batchUpdateTimesheetStatuses } from '../../../api/services/timesheetProofingService';
import { getTimesheetStatusesUserId, getTimesheetById } from '../../../api/services/timesheetService';
import { getDistricts } from '../../../api/services/districtService';

//Types/Data
import { TimesheetStatus } from '../../../common/constants';
import TimesheetWeek from '../../../api/models/timesheetWeek';
import TimesheetSummary from '../../../api/models/timesheetSummary';
import TimesheetSubmitRequest from '../../../api/requests/timesheetSubmitRequest';
import TimesheetStatusesResponse from '../../../api/responses/timesheetStatusesResponse';
import TimesheetEntryFlag from '../../../api/models/timesheetEntryFlag';
import WeekItem from '../../../api/models/timesheetWeekItem';
import TimesheetFlagNote from '../../../api/models/timesheetFlagNote';
import TimesheetFlag from '../../../api/models/timesheetFlag';
import { setTimesheetUnlockRequestStatus } from '../../../api/services/timesheetUnlockService';
import SetTimesheetUnlockRequestStatusData from '../../../api/requests/setTimesheetUnlockRequestStatusData';

//Components
import Layout from '../../../components/layout';
import Modal from '../../../components/modal';
import TimesheetWeekEntries from '../../../components/timesheetWeekEntries';
import { Notification } from '../../../components/notifications';
import SelectedTimesheet from '../../../api/models/SelectedTimesheetModel';
import { DataResponse } from '../../../api/responses/baseResponse';

//Utils
import { daysOfWeek } from '../../../common/utils';
import DisplayFor from '../../../components/displayFor';
import EditEntry from '../../../components/logTimeModal/types/editEntry';
import { getCurrentWeekDates } from '../../timesheets';
import { TimesheetUnlockStatusLookup } from '../../../common/constants';
import TimesheetDistrict from '../../../api/models/timesheetDistrict';
import DistrictsResponse from '../../../api/responses/districtsResponse';
//GET params
type Params = { id: string; };

export type SelectedRange = {
  startDate: Date;
  endDate: Date;
  week: TimesheetWeek;
};

const getTimesheetsDefer = async (args: any[]) => {
  return (typeof args[0].disciplineId !== "undefined" && args[0].disciplineId !== -1)
    ? getTimesheetStatusesUserId(args[0].disciplineId, args[0].startDate, args[0].endDate, args[0].numberOfMonths, args[0].userId)
    : null;
}

/**
 * Page for displaying timesheets and entering time
 * @returns 
 */
const ViewTimesheet = () => {
  const { id } = useParams<Params>();
  const userRoles = useAppSelector((s) => s.user.roles);

  const now = new Date();

  // UI States
  const { monday, sunday } = getCurrentWeekDates();
  const [startDate, setStartDate] = useState<Date | null>(monday);
  const [endDate, setEndDate] = useState<Date | null>(sunday);  
  const [disciplineName, setDisciplineName] = useState('');
  const [selectedRange, setSelectedRange] = useState<SelectedRange | null>();
  const [selectedWeek, setSelectedWeek] = useState<TimesheetWeek>();
  const [timesheets, setTimesheets] = useState<TimesheetSummary[]>();
  const [editEntry, setEditEntry] = useState<EditEntry>();
  const [reviewTimesheet, setReviewTimesheet] = useState<TimesheetSummary>();
  const [activeTimesheet, setActiveTimesheet] = useState<TimesheetSummary>();
  const [submitMessage, setSubmitMessage] = useState('');
  const [tsStatus, setTsStatus] = useState<number>(0);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const [flagNotes, setFlagNotes] = useState<TimesheetFlagNote[]>();
  const [showFlags, setShowFlags] = useState<boolean>(true);
  const [isInReview, setIsInReview] = useState<boolean>(false);
  const [therapistName, setTherapistName] = useState('');
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [unlockFlag, setUnlockFlag] = useState<boolean>(false);
  const [isPending, setIsPending] = useState<boolean>(false);
  const [showChangeAffect, setShowChangeAffect] = useState<boolean>(false);
  const [changeAffect, setChangeAffect] = useState<string>('');
  const [districts, setDistricts] = useState<TimesheetDistrict[]>();

  useAsync({
    promiseFn: getDistricts,
    onResolve: (response: DistrictsResponse) => setDistricts(
      (response.data?.districts?.filter(x => x.isVisible) ?? []).map(d => ({
        districtId: d.districtId,
        name: d.name,
        isVisible: d.isVisible,
        dailyAllowance: d.dailyAllowance,
        dailyOverageAllowance: d.dailyOverageAllowance,
        unlimitedOverageAllowed: d.unlimitedOverageAllowed,
        allowsDistrictTime: d.allowsDistrictTime,
        enableWeekendBilling: d.enableWeekendBilling ?? false,
        division: d.division,
        programs: d.programs,
        schools: d.schools,
      } as TimesheetDistrict))
    )
  });

  const refreshTimesheetSelection = (response: TimesheetStatusesResponse | null) => {
    let targetStartDate = startDate;
    let targetEndDate = endDate;
    let targetWeekId = selectedRange?.week?.weekId;

    let timesheet = response?.data?.timesheets.find(t => t.start === targetStartDate && t.end === targetEndDate);

    let week = (targetWeekId === -1)
      ? timesheet?.weeks.find(w =>
        w.firstDayOfWeek === selectedRange?.week.firstDayOfWeek &&
        w.lastDayOfWeek === selectedRange?.week.lastDayOfWeek)
      : timesheet?.weeks.find(w => w.weekId === targetWeekId);

    if (week) {
      setSelectedRange({
        startDate: targetStartDate!,
        endDate: targetEndDate!,
        week: week
      });
      setSelectedWeek(current => week);
    }
  }

  const timesheetsRequest = useAsync({
    deferFn: getTimesheetsDefer,
    onResolve: (response: TimesheetStatusesResponse | null) => {
      setTimesheets([...response?.data?.timesheets || []]);
      refreshTimesheetSelection(response);
    },
  });

    const fetchTimesheetById = async (id: string) => {
    setIsPending(true)
    try {
      const response = await getTimesheetById(Number(id));
      const data = response.data;
      setIsPending(false);
      // Setting the discipline and therapist names
      setDisciplineName(data.disciplineName);
      setTherapistName(`${data.therapistLastName}, ${data.therapistFirstName}`);

       // Running the timesheets request with the necessary parameters
       await timesheetsRequest.run({
        disciplineId: data.disciplineId,
        startDate: new Date(data.startDate),
        endDate: new Date(data.endDate),
        numberOfMonths: 1,
        userId: data.userId,
      });
    } catch (error) {
      setIsPending(false);
      console.error("Error fetching timesheet:", error);
    }
  };

  const handleSubmit = async (args: any[]) => {
    const timesheetId: number = args[0] ?? Number(id);

    //Create our status object
    const status = {
      timesheetStatusId: TimesheetStatus.Submitted ?? 0,
      name: TimesheetStatus[TimesheetStatus.Submitted]
    }

    //Create our submit request
    const request: TimesheetSubmitRequest = {
      timesheetId: timesheetId,
      timesheetStatus: status
    }

    //Submit the request
    const response = await submitTimesheet(request);

    //Make sure the response was successful
    if (response) {
      //Set notification message
      setSubmitMessage('Timesheet submitted!');
      setIsSubmitSuccess(true);

      //Reset the review timesheet to switch views
      setReviewTimesheet(undefined);

      //Clear out the selected date ranges so that the review timesheet does not appear after submission
      setSelectedRange(null);

      //Copy our timesheets array
      const sheets: TimesheetSummary[] = timesheets ?? [];
      let sheetIndex: number | null = null;

      //Loop through timesheets to find the one we want to replace
      sheets.map((ts, i) => {
        //If this is the correct sheet, save the index
        if (ts.timesheetId === request.timesheetId) {
          sheetIndex = i;
          return i;
        }
        return i;
      });

      //Verify we have a correct index
      if (sheetIndex !== null && sheets[sheetIndex] !== undefined && sheets[sheetIndex] !== null) {
        //Update the temporary timesheet array
        sheets[sheetIndex].status.timesheetStatusId = TimesheetStatus.Submitted ?? 0;
        sheets[sheetIndex].status.name = TimesheetStatus[TimesheetStatus.Submitted];

        //Update our timesheets array
        setTimesheets(sheets);
      }

      return new Promise<void>((resolve) => resolve());
    }

    setSubmitMessage('Error submitting timesheet');
    setIsSubmitSuccess(false);

    return new Promise<void>((resolve) => resolve());
  }

  const handleSubmitAsync = useAsync({ deferFn: handleSubmit });

  const handleApprove = async (args: any[]) => {
    const timesheetId: number = args[0] ?? Number(id);

    // Always use the batch endpoint which triggers the SP
    await batchUpdateTimesheetStatuses([timesheetId], showChangeAffect && changeAffect ? changeAffect : undefined);

    setSubmitMessage('Timesheet Approved!');
    setIsSubmitSuccess(true);
    setReviewTimesheet(undefined);
    setSelectedRange(null);
    setChangeAffect('');
    setShowChangeAffect(false);

    const sheets: TimesheetSummary[] = timesheets ?? [];
    const sheetIndex = sheets.findIndex(s => s.timesheetId === timesheetId);
    if (sheetIndex >= 0) {
      sheets[sheetIndex].status.timesheetStatusId = TimesheetStatus.Approved;
      sheets[sheetIndex].status.name = TimesheetStatus[TimesheetStatus.Approved];
      setTimesheets([...sheets]);
    }
  }

  const doApproveTimesheet = async (timesheetId: number) => {
    //Create our status object
    const status = {
      timesheetStatusId: TimesheetStatus.Approved ?? 0,
      name: TimesheetStatus[TimesheetStatus.Approved]
    }

    //Create our submit request
    const request: TimesheetSubmitRequest = {
      timesheetId: timesheetId,
      timesheetStatus: status
    }

    //Submit the request
    const response = await submitTimesheet(request);

    //Make sure the response was successful
    if (response) {
      //Set notification message
      setSubmitMessage('Timesheet Approved!');
      setIsSubmitSuccess(true);

      //Reset the review timesheet to switch views
      setReviewTimesheet(undefined);

      //Clear out the selected date ranges so that the review timesheet does not appear after submission
      setSelectedRange(null);

      //Copy our timesheets array
      const sheets: TimesheetSummary[] = timesheets ?? [];
      let sheetIndex: number | null = null;

      //Loop through timesheets to find the one we want to replace
      sheets.map((ts, i) => {
        //If this is the correct sheet, save the index
        if (ts.timesheetId === request.timesheetId) {
          sheetIndex = i;
          return i;
        }
        return i;
      });

      //Verify we have a correct index
      if (sheetIndex !== null && sheets[sheetIndex] !== undefined && sheets[sheetIndex] !== null) {
        //Update the temporary timesheet array
        sheets[sheetIndex].status.timesheetStatusId = sheets[sheetIndex].status.timesheetStatusId === TimesheetStatus.Submitted ? TimesheetStatus.Approved : TimesheetStatus.Submitted;
        sheets[sheetIndex].status.name = sheets[sheetIndex].status.timesheetStatusId === TimesheetStatus.Submitted ? TimesheetStatus[TimesheetStatus.Approved] : TimesheetStatus[TimesheetStatus.Submitted];

        //Update our timesheets array
        setTimesheets(sheets);
      }

      return new Promise<void>((resolve) => resolve());
    }

    setSubmitMessage('Error submitting timesheet');
    setIsSubmitSuccess(false);

    return new Promise<void>((resolve) => resolve());
  }

  const handleApproveAsync = useAsync({ deferFn: handleApprove });

  const getTotalHours = (timesheet: TimesheetSummary) => {
    let total = 0;
    timesheet.weeks.forEach((tw) => {
      tw.items.forEach((wi) => {
        wi.tasks.forEach((task) => {
          task.entries.forEach((entry) => {
            if (entry.minutes !== undefined && entry.minutes !== null && entry.minutes > 0)
              total += entry.minutes;
          });
        });
      });
    });

    //convert the mins to hours
    return Number((total / 60).toFixed(2)).valueOf()
  }

  /**
   * Handles a click on the timesheet.
   * Argument is a week item
   * @param editEntry
   */
  const handleTimesheetFlagModal = (editEntry: any) => {
    //setShowFlags(false);
  }

  /**
     * Returns true if any of the entries in a given week are flagged
     * @param items 
     * @param day 
     * @returns boolean
     */
  const isWeekFlagged = (items: WeekItem[]) => {
    let flags: any[] = [];

    items.forEach((item) => {
      item.tasks.forEach((task) => {
        task.entries.forEach(entry => {
          if (entry.timesheetEntryFlags.length > 0) {
            flags.push(...entry.timesheetEntryFlags);
          }
        });
      });
    });

    return flags.length > 0;
  }

  const sendApprove = async (args: any) => {
    if (!args[0])
      setError("Timesheet unlock request is not selected");

    const data: SetTimesheetUnlockRequestStatusData = {
      status: TimesheetUnlockStatusLookup.Accepted,
      timesheetId: args[0]
    }

    return await setTimesheetUnlockRequestStatus(data);
  }

  const approve = useAsync({
    deferFn: sendApprove,
    onResolve: async (response: any) => {
      setUnlockFlag(false);
      setMessage('Timesheet unlock request successfully approved');
      await fetchTimesheetById(id); 
    },
    onReject: (data) => {
      setMessage(data.message);
     }
  });

  useEffect(() => {  
    if(id){
      fetchTimesheetById(id);
    }
  },[id])

  // When activeTimesheet changes, check if it has decreased hours
  useEffect(() => {
    const tsId = activeTimesheet?.timesheetId ?? (timesheets?.length === 1 ? timesheets[0].timesheetId : undefined);
    if (tsId) {
      hasDecreasedHours(tsId)
        .then(r => {
          setShowChangeAffect(r.data === true);
          if (r.data !== true) setChangeAffect('');
        })
        .catch(() => {
          setShowChangeAffect(false);
          setChangeAffect('');
        });
    } else {
      setShowChangeAffect(false);
      setChangeAffect('');
    }
  }, [activeTimesheet, timesheets]);

  return (
    <Layout isLoading={
      isPending ||
      timesheetsRequest.isPending ||
      handleSubmitAsync.isPending ||
      handleApproveAsync.isPending
    }>
      <div className={timesheetsRequest.isPending ? 'hidden' : 'block'}>
        <Modal
          saveText='Ok'
          open={(flagNotes && flagNotes.length > 0)}
          onCancel={() => { setFlagNotes(undefined); }}
          onSave={() => { setFlagNotes(undefined); }}
        >
          <Fragment>
            <ul>
              {flagNotes?.filter(n => n.note !== undefined).map((note, i) => (
                <li key={"fnd_" + i}>
                  "{note.note}" <br />
                  <span className='pl-4 text-sm italic text-gray-500'>
                    ~ {note.authorFirstName ?? ""} {moment(note.createdDate).fromNow()}
                  </span>
                </li>
              ))}
            </ul>
          </Fragment>
        </Modal>
        <div className='flex'>
          {/* Left Column: DateRange picker*/}
          <Fragment>
            <div className='space-y-5 w-72'>
              <div className='justify-between discipline-picker'>
                <div className='font-bold'>{therapistName}</div>
                <span className='font-bold'>{disciplineName}</span>
                {/* Change Affect dropdown - shown inline for BillingSpecialist when timesheet has decreased hours */}
                <DisplayFor roles={[Role.BillingSpecialist]}>
                  <>
                    {showChangeAffect && (
                      <div className='mt-2' style={{ minWidth: '180px' }}>
                        <label className='text-xs font-bold text-gray-600'>Change Affect</label>
                        <Select
                          className='block'
                          styles={graySelectStyles}
                          isMulti={false}
                          options={ChangeAffectOptions as any}
                          isClearable={true}
                          placeholder='Select...'
                          value={ChangeAffectOptions.find((o: any) => o.value === changeAffect) || null}
                          onChange={(e: any | null) => {
                            setChangeAffect(e ? e.value : '');
                          }}
                        />
                      </div>
                    )}
                  </>
                </DisplayFor>
              </div>
              {/* Timesheet dates organized by week */}
              {timesheets?.map((ts, ti) => (
                <div
                  key={`timesheet_${ti}`}
                  className='p-3 bg-white rounded-lg shadow timesheet'
                >
                  <div className='flex items-center justify-center timesheet-status'>
                    {ts.status?.timesheetStatusId === TimesheetStatus.InProgress && (
                      <DisplayFor roles={[Role.BillingSpecialist]}>
                        <>
                          <button className='py-1 mr-2 text-sm primary button'
                            disabled={showChangeAffect && !changeAffect}
                            onClick={(e) => {
                              handleApproveAsync.run(ts.timesheetId)
                            }}>Approve</button>
                          <button className='py-1 text-sm bg-ptsblue-light hover:bg-ptsblue-normal'
                            onClick={(e) => {
                              handleSubmitAsync.run(ts.timesheetId)
                            }}>Submit</button>
                        </>
                      </DisplayFor>
                    )}

                    {ts.status?.timesheetStatusId === TimesheetStatus.Submitted && (
                      <DisplayFor roles={[Role.BillingSpecialist]}>
                        <button className='py-1 mr-2 text-sm primary button'
                          disabled={showChangeAffect && !changeAffect}
                          onClick={(e) => {
                            handleApproveAsync.run(ts.timesheetId)
                          }}>Approve</button>
                      </DisplayFor>
                    )}

                    {ts.status?.timesheetStatusId === TimesheetStatus.Submitted && (
                      <button className='py-1 text-sm italic text-gray-500 bg-white hover:bg-white hover:text-black'>
                        Submitted
                      </button>
                    )}

                    {ts.status?.timesheetStatusId === TimesheetStatus.Approved && (
                      <button className='py-1 text-sm'>Approved</button>
                    )}

                    {(ts.status?.timesheetStatusId === TimesheetStatus.Rejected) && (
                      <button className='py-1 text-sm bg-red-600 hover:bg-red-800'>
                        Rejected
                      </button>
                    )}

                    {ts.status === null && (
                      <button className='py-1 text-sm italic text-gray-500 bg-white hover:bg-white hover:text-black'>
                        Idle
                      </button>
                    )}
                  </div>

                  {(ts.status?.timesheetStatusId === TimesheetStatus.Rejected || ts.showFlags === true) && (
                    <div
                      className='p-2 mt-3 text-sm text-white bg-red-500 rounded-lg cursor-pointer'
                      onClick={() => { if (ts.flagNotes?.filter(n => n.note !== undefined).length > 0) setFlagNotes(ts.flagNotes.filter(n => n.note !== undefined)); }} >
                      {ts.flagNotes?.filter(n => n.note !== undefined).length === 0 && (
                        <Fragment>
                          Your timesheet was rejected by a Billing Specialist but no feedback was entered. Please message your Billing Specialist for more details.
                        </Fragment>
                      )}

                      {ts.flagNotes?.filter(n => n.note !== undefined).length > 0 && (
                        <Fragment>
                          Your timesheet was rejected by a Billing Specialist, please click here to view the feedback/corrections needed.
                        </Fragment>
                      )}
                    </div>
                  )}

                  <div className='mt-2 timesheet-weeks-nav'>
                    {ts.weeks.map((w, wi) => (
                      <div
                        key={`week_${wi}`}
                        className={cx(
                          wi % 2 !== 0 ? 'bg-gray-100' : 'bg-white',
                          'p-2 flex justify-between hover:bg-gray-200 cursor-pointer'
                        )}
                        onClick={() => {
                          if (ts) {
                            if (ts.timesheetId !== null) setTsStatus(ts.status.timesheetStatusId);
                            setReviewTimesheet(undefined);
                            setIsInReview(current => false);
                            setShowFlags(true);
                            setActiveTimesheet(current => ts);
                            setUnlockFlag(ts.isLocked!);
                            setSelectedRange({
                              startDate: ts.start,
                              endDate: ts.end,
                              week: w,
                            });
                            setSelectedWeek(current => w);
                          }
                        }}
                      >
                        <div className={`text-sm ${ts?.showFlags && isWeekFlagged(w.items) ? 'font-bold text-red-600' : ''}`}>{w.title} {(w.items?.length > 0) ? <span className='italic'>({w.items.length})</span> : <span></span>}</div>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          className={cx(
                            'h-6 w-6 text-ptsgreen-dark',
                            w.hasIssues ? 'block' : 'hidden'
                          )}
                          viewBox='0 0 20 20'
                          fill='currentColor'
                        >
                          <path
                            fillRule='evenodd'
                            d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                            clipRule='evenodd'
                          />
                        </svg>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Fragment>

          {/* Right Column: Where timesheets are displayed */}
          {!reviewTimesheet &&
            !isInReview && (
              <div className='flex-grow p-3 ml-5 bg-white rounded-lg shadow'>
                {submitMessage && (
                  <Notification
                    message={submitMessage}
                    isSuccess={isSubmitSuccess}
                    isOpen={submitMessage !== '' && submitMessage !== undefined && submitMessage !== null}
                  />
                )}

                {!selectedRange && <div className='text-center text-gray-500'>Select a week to begin.</div>}
                {selectedRange && (
                  <div>
                    {/* Timesheet Flags */}
                    {activeTimesheet &&
                      activeTimesheet.timesheetFlags !== null &&
                      activeTimesheet.timesheetFlags?.filter((timesheetFlag: TimesheetFlag) => timesheetFlag.timesheetWeekId === selectedWeek?.weekId).length! > 0 &&
                      activeTimesheet.showFlags &&
                      showFlags &&
                      <Notification isSuccess={false} isUserCloseable={false}>
                        <ul className='pl-5 list-disc'>
                          {activeTimesheet.timesheetFlags
                            ?.filter((timesheetFlag: TimesheetFlag) => timesheetFlag.timesheetWeekId === selectedWeek?.weekId)
                            ?.map((timesheetFlag: TimesheetFlag) => (
                              <Fragment key={`timesheet-flag-${timesheetFlag.timesheetFlagId}`}>
                                {((timesheetFlag && timesheetFlag !== undefined) &&
                                  (timesheetFlag.proofingRule !== null) ?
                                  <li>
                                    <span
                                      className='font-bold text-white underline cursor-pointer hover:text-black'
                                      onClick={() => {
                                        handleTimesheetFlagModal(timesheetFlag);
                                      }}>
                                      {timesheetFlag.proofingRule?.name ?? 'Proofing rule not found'}
                                    </span>
                                    <span> -- Created: {moment(timesheetFlag.dateFlagged).format('l')}</span>
                                  </li>
                                  :
                                  <></>
                                )
                                }
                              </Fragment>
                            ))}
                        </ul>
                      </Notification>
                    }
                    {activeTimesheet &&
                      activeTimesheet.flags !== null &&
                      (activeTimesheet.flags?.length ?? 0) > 0 &&
                      activeTimesheet.showFlags &&
                      showFlags &&
                      activeTimesheet.flags?.filter((timesheetFlag: TimesheetEntryFlag) => timesheetFlag.timesheetWeekId === selectedWeek?.weekId).length! > 0 &&
                      <Notification isSuccess={false} isUserCloseable={false}>
                        <ul className='pl-5 list-disc'>
                          {activeTimesheet.flags?.filter((timesheetFlag: TimesheetEntryFlag) => timesheetFlag.timesheetWeekId === selectedWeek?.weekId)
                            ?.map((timesheetFlag: TimesheetEntryFlag) => (
                              <Fragment key={`timesheet-flag-${timesheetFlag.timesheetEntryFlagId}`}>
                                {activeTimesheet.flags?.filter(f => {
                                  return moment(f.dateFlagged).isBetween(selectedRange.week.firstDayOfWeek, selectedRange.week.firstDayOfWeek);
                                })}
                                {((timesheetFlag && timesheetFlag !== undefined) &&
                                  (timesheetFlag.proofingRule !== null) ?
                                  <li>
                                    <span
                                      className='font-bold text-white underline cursor-pointer hover:text-black'
                                      onClick={() => {
                                        handleTimesheetFlagModal(timesheetFlag);
                                      }}>
                                      {timesheetFlag.proofingRule?.name ?? 'Proofing rule not found'}
                                    </span>
                                    <span> -- Created: {moment(timesheetFlag.dateFlagged).format('l')}</span>
                                  </li>
                                  :
                                  <></>
                                )
                                }
                              </Fragment>
                            ))}
                        </ul>
                      </Notification>
                    }
                    <div>
                    <div>
                    {(userRoles?.includes(Role.BillingSpecialist) && activeTimesheet?.isLocked && unlockFlag && (activeTimesheet?.status?.name != "Approved")) && (
                        <div className={`center`} style={{ textAlign: 'center' }}>
                          <div className='text-2xl font-bold' style={{ color: '#f59e57' }}>Period Locked</div>
                              <div>
                                <a style={{ color: 'cornflowerblue', cursor: 'pointer' }}
                                 onClick={() => {
                                    approve.run(activeTimesheet.timesheetId);
                                  }}>
                                  Unlock
                                </a>
                              </div>
                      
                        </div>
                      )
                      }
                        </div>
                      <div className='inline-block w-1/2'>
                        <div className='h-3 text-sm text-gray-500'>Viewing:</div>
                        <div className='text-2xl font-bold h-7'>{selectedRange.week.title}</div>
                      </div>
                      <div className='inline-block w-1/2 align-top'>
                        {/*Enter Time*/}
                      </div>
                    </div>

                    <TimesheetWeekEntries
                      week={selectedRange.week}
                      displayFlags={activeTimesheet?.showFlags}
                      allowEdit={false}
                      editEntry={(editModel) => { setEditEntry(editModel); }}
                      timesheet={activeTimesheet}
                      districts={districts} />
                  </div>
                )}
              </div>
            )}

          {/* Right Column: Review timesheet display */}
          {reviewTimesheet &&
            !isInReview && (
              <div className='flex-grow p-3 ml-5 bg-white rounded-lg shadow'>
                <div className='text-3xl text-gray-500'>Timesheet Overview</div>

                <div className='ml-1'>
                  <b>Total Hours Logged:</b> {getTotalHours(reviewTimesheet)}h <br />
                  <b>Date of submission:</b> {moment().format("MM/DD/YYYY")} <br />
                </div>

                {reviewTimesheet.weeks.map((tw, ti) =>
                  <Fragment key={`tw_key_${ti}`}>
                    {daysOfWeek(tw.firstDayOfWeek).map((day, i) => {
                      const isWeekend = day.isoWeekday() === 6 || day.isoWeekday() === 7;
                      const hasWeekendDistrict = isWeekend && tw.items.some(item => {
                        const dist = districts?.find(d => d.districtId === item.districtId);
                        return !dist || dist.enableWeekendBilling;
                      });
                      const hasExistingWeekendEntries = isWeekend && tw.items.some(item =>
                        item.tasks.some(task =>
                          task.entries.some(entry => {
                            const d = moment(entry.dateOfEntry).isoWeekday();
                            return (d === 6 || d === 7) && (entry.minutes ?? 0) > 0 && moment(entry.dateOfEntry).isSame(day, 'day');
                          })
                        )
                      );
                      if (isWeekend && !hasWeekendDistrict && !hasExistingWeekendEntries) return null;
                      return (
                      <Fragment key={`twa_key_${i}`}>
                        {tw.items.some(items =>
                          items.tasks.some(task =>
                            task.entries.some(entry =>
                              moment(entry.dateOfEntry).isSame(day)
                            )
                          )
                        ) && (
                            <div className='ml-5'>
                              <div className='mt-4 mb-3 text-xl font-bold'>{day.format("M/D dddd")}</div>
                              {tw.items.filter(items =>
                                items.tasks.some(task =>
                                  task.entries.some(entry =>
                                    moment(entry.dateOfEntry).isSame(day)
                                  )
                                )
                              ).map((item, tai) =>
                                <div key={`twi_key_${tai}`} className='mt-2 mb-5 ml-5'>
                                  <div className='font-bold'>{item.title}</div>
                                  {item.tasks.filter(t => t.entries.some(e => moment(e.dateOfEntry).isSame(day))).map((task, i) =>
                                    <Fragment key={`task${i}`}>
                                      <div className='mt-2 ml-5'>{task.entryTypeId !== 3 ? task.taskTypeName : "Group Time"} ({task.entries.find(e => day.isSame(moment(e.dateOfEntry)))?.minutes}m)</div>
                                      {/* Only display notes if notes were entered */}
                                      {task.entries.find(e => day.isSame(moment(e.dateOfEntry)))?.notes !== "" && (
                                        <div className='mt-2 ml-10' style={{ maxWidth: '45rem' }}>{task.entries.find(e => day.isSame(moment(e.dateOfEntry)) && e.notes !== "")?.notes ?? "No notes"}</div>
                                      )}
                                    </Fragment>
                                  )}
                                </div>
                              )
                              }

                              <hr />
                            </div>
                          )}
                      </Fragment>
                    );
                    })}
                  </Fragment>
                )}
              </div>
            )}
        </div>
      </div>
    </Layout>
  );
};

export default ViewTimesheet;
